import { useMemo } from "react";
import type { DBProject, DBInvoice, DBVoucher } from "@/lib/db-types";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import { isTaskLine } from "@/lib/billing-calculations";

export interface ProjectClosingValidation {
  openTasks: number;
  pendingVouchers: number;
  invoicedTotal: number;
  contractAmount: number;
  invoiceDiff: number;
  hasInvoiceDiff: boolean;
  hasFinalInvoice: boolean;
  bfEstimated: number;
  bfActual: number | null;
  bfDiff: number | null;
  blockers: {
    tasks: boolean;
    vouchers: boolean;
    finalInvoice: boolean;
  };
}

export interface ProjectClosingInput {
  project: Pick<DBProject, "total_amount" | "bf_actual"> | null | undefined;
  budgetLines: BudgetLineRow[] | null | undefined;
  vouchers: DBVoucher[] | null | undefined;
  invoices: DBInvoice[] | null | undefined;
  bfEstimated: number | null | undefined;
}

const EMPTY_VALIDATION: ProjectClosingValidation = {
  openTasks: 0,
  pendingVouchers: 0,
  invoicedTotal: 0,
  contractAmount: 0,
  invoiceDiff: 0,
  hasInvoiceDiff: false,
  hasFinalInvoice: false,
  bfEstimated: 0,
  bfActual: null,
  bfDiff: null,
  blockers: { tasks: false, vouchers: false, finalInvoice: true },
};

export function calcProjectClosingValidation(
  input: ProjectClosingInput,
): ProjectClosingValidation {
  const { project, budgetLines, vouchers, invoices, bfEstimated } = input;
  if (!project || !budgetLines || !vouchers || !invoices) return EMPTY_VALIDATION;

  const openTasks = budgetLines.filter(isTaskLine).reduce((sum, bl) => {
    return sum + bl.assignments.filter((a) => a.task_status !== "approved").length;
  }, 0);

  // Erik-beslutning 2026-06-02: forwarded_to_dl blokkerer IKKE lukking.
  // Kun bilag som venter PL-godkjenning (pending_approval) teller som blokkering.
  // "pending" er utdødd legacy-status og skal ikke brukes.
  const pendingVouchers = vouchers.filter((v) => v.status === "pending_approval").length;

  // Kontraktssum (project.total_amount) er eks. mva, så fakturasum må også
  // være eks. mva for at differansen skal stemme. amount_excl_vat populeres
  // av både create-invoice og sync-poweroffice-invoices; fallback til amount
  // for legacy-rader uten verdi.
  const invoicedTotal = invoices.reduce(
    (s, i) => s + Number(i.amount_excl_vat ?? i.amount ?? 0),
    0,
  );
  const contractAmount = Number(project.total_amount ?? 0);
  const invoiceDiff = contractAmount - invoicedTotal;
  const hasInvoiceDiff = Math.abs(invoiceDiff) >= 1;

  const hasFinalInvoice = invoices.some((i) => i.invoice_type === "final");

  const bfEst = bfEstimated ?? 0;
  const bfActual = project.bf_actual != null ? Number(project.bf_actual) : null;
  const bfDiff = bfActual != null ? bfActual - bfEst : null;

  return {
    openTasks,
    pendingVouchers,
    invoicedTotal,
    contractAmount,
    invoiceDiff,
    hasInvoiceDiff,
    hasFinalInvoice,
    bfEstimated: bfEst,
    bfActual,
    bfDiff,
    blockers: {
      tasks: openTasks > 0,
      vouchers: pendingVouchers > 0,
      finalInvoice: !hasFinalInvoice,
    },
  };
}

export function useProjectClosingValidation(
  input: ProjectClosingInput,
): ProjectClosingValidation {
  const { project, budgetLines, vouchers, invoices, bfEstimated } = input;
  return useMemo(
    () => calcProjectClosingValidation({ project, budgetLines, vouchers, invoices, bfEstimated }),
    [project, budgetLines, vouchers, invoices, bfEstimated],
  );
}
